import type { Year } from "@/features/pe_aldd";
import { Empty } from "antd";
import { Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CategoricalChartState } from "recharts/types/chart/types";
import { AnimationTiming } from "recharts/types/util/types";
import { TICK_YEARS, xAxisBaseProps, Y_AXIS_LABEL_TOP_OFFSET, yAxisBaseProps, yAxisLabelProps } from "./axis";
import { CustomReferenceLabel } from "./CustomReferenceLabel";
import { CustomTooltip } from "./CustomTooltip";
export interface DataSeries {
  key: string;
  color: string;
  name?: string;
  strokeDasharray?: string;
  alwaysSolid?: boolean;
  band?: {
    from: string;
    to: string;
    fromColor?: string;
    toColor?: string;
  };
  contextId?: string;
  emphasisStrokeWidth?: number;
  // width when this series is not the selected one; omitting it keeps the shared defaults
  baseStrokeWidth?: number;
}
interface LegendContentProps {
  series: DataSeries[];
}
interface LineChartProps {
  /** Y-axis tick label. The caller knows what the numbers mean. */
  formatTick: (value: number) => string;
  chartData: Record<string, unknown>[];
  series: DataSeries[];
  /** Tooltip value, unit and precision included. */
  formatValue: (value: number) => string;
  metricLabel?: string;
  selectedDataKeys: string[];
  selectedYear?: Year;
  onYearChange?: (year: Year) => void;
  onSeriesClick?: (key: string) => void;
  legendContent?: React.ComponentType<LegendContentProps>;
  emptyMessage?: string | null;
  showArea?: boolean;
  showReferenceLine?: boolean;
  stacked?: boolean;
  isAnimationActive?: boolean;
  yAxisDomain?: [number, number];
  columnKeySuffix?: string;
  columnLabels?: [React.ReactNode, React.ReactNode];
  tag?: React.ReactNode;
}
const DefaultLegendContent = ({
  series
}: LegendContentProps) => {
  return <div className="flex gap-4 pb-1" data-sentry-component="DefaultLegendContent" data-sentry-source-file="LineChart.tsx">
      <div className="flex items-center gap-4">
        {series.map(item => <div key={item.key} className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-sm" style={{
          backgroundColor: item.color
        }} />
            <span className="text-sm tracking-wide">{item.name ?? item.key}</span>
          </div>)}
      </div>
    </div>;
};
export const LineChart = ({
  formatTick,
  chartData,
  series,
  formatValue,
  metricLabel,
  selectedDataKeys,
  selectedYear,
  onYearChange,
  onSeriesClick,
  legendContent: LegendContent = DefaultLegendContent,
  emptyMessage = "Data not available",
  showArea = true,
  showReferenceLine = true,
  stacked = false,
  isAnimationActive = true,
  columnKeySuffix,
  columnLabels,
  tag,
  yAxisDomain
}: LineChartProps) => {
  // SVG attribute, so currentColor rather than var(): the reference line follows the mode.
  const TICK_COLOR = "currentColor";
  const GRID_COLOR = "currentColor";
  const ANIMATION_DURATION = 240;
  const ANIMATION_EASING: AnimationTiming = "ease";
  const handleChartClick = (state: CategoricalChartState) => {
    const currentYear = state?.activeLabel;
    if (onYearChange && currentYear) {
      onYearChange(Number(currentYear) as Year);
    }
  };
  const renderBands = () => {
    const bandItems = series.filter(item => item.band);
    if (!bandItems.length) return null;
    return <>
        <defs data-sentry-element="defs" data-sentry-source-file="LineChart.tsx">
          {bandItems.map(item => <linearGradient key={`band-gradient-${item.key}`} id={`band-gradient-${item.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={item.band!.fromColor ?? item.color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={item.band!.toColor ?? item.color} stopOpacity={0.3} />
            </linearGradient>)}
        </defs>
        {bandItems.map(item => <Area key={`band-${item.key}`} dataKey={(row: Record<string, unknown>) => {
        const from = row[item.band!.from];
        const to = row[item.band!.to];
        return from == null || to == null ? null : [from, to];
      }} fill={`url(#band-gradient-${item.key})`} stroke="none" strokeWidth={0} legendType="none" activeDot={false} isAnimationActive={isAnimationActive} animationEasing={ANIMATION_EASING} animationDuration={ANIMATION_DURATION} />)}
      </>;
  };

  // prevent (complete) overlap of lines in chart, increase stroke width
  // emphasised data line will always be on top
  const rankForDraw = (key: string) => {
    const i = selectedDataKeys.indexOf(key);
    return i === -1 ? -1 : i;
  };
  const drawOrderedSeries = [...series].filter(item => !item.band).sort((a, b) => rankForDraw(a.key) - rankForDraw(b.key));
  const renderAreaGradient = () => {
    const areaKey = selectedDataKeys.length && selectedDataKeys[0];
    const areaColor = series.find(s => s.key === areaKey)?.color;
    return <>
        <Area key={`area-${areaKey}`} dataKey={areaKey} fill={`url(#gradient-${areaKey})`} stroke="none" legendType="none" isAnimationActive={isAnimationActive} animationEasing={ANIMATION_EASING} animationDuration={ANIMATION_DURATION} hide={!!selectedDataKeys.length && !showArea} data-sentry-element="Area" data-sentry-source-file="LineChart.tsx" />
        <defs data-sentry-element="defs" data-sentry-source-file="LineChart.tsx">
          <linearGradient key={`gradient-${areaKey}`} id={`gradient-${areaKey}`} x1="0" y1="0" x2="0" y2="1" data-sentry-element="linearGradient" data-sentry-source-file="LineChart.tsx">
            <stop offset="0%" stopColor={areaColor} stopOpacity={0.2} data-sentry-element="stop" data-sentry-source-file="LineChart.tsx" />
            <stop offset="75%" stopColor={areaColor} stopOpacity={0} data-sentry-element="stop" data-sentry-source-file="LineChart.tsx" />
          </linearGradient>
        </defs>
      </>;
  };
  return <div className="h-full w-full" data-sentry-component="LineChart" data-sentry-source-file="LineChart.tsx">
      <ResponsiveContainer data-sentry-element="ResponsiveContainer" data-sentry-source-file="LineChart.tsx">
        {!chartData.length ? <div className="flex h-full items-center justify-center text-gray-500">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyMessage} />
          </div> : <ComposedChart data={chartData} margin={{
        top: metricLabel ? Y_AXIS_LABEL_TOP_OFFSET : 0,
        right: 0,
        left: 0,
        bottom: 0
      }} onClick={handleChartClick}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} strokeOpacity={0.15} />

            <Legend align="left" verticalAlign="top" content={() => <LegendContent series={series} />} />

            <XAxis {...xAxisBaseProps} dataKey="year" ticks={TICK_YEARS} />
            <YAxis {...yAxisBaseProps} tickFormatter={val => formatTick(val as number)} domain={stacked ? [0, 100] : yAxisDomain || ["auto", "auto"]} ticks={stacked ? [0, 25, 50, 75, 100] : undefined} allowDataOverflow={yAxisDomain !== undefined} label={metricLabel ? yAxisLabelProps(metricLabel) : undefined} />

            <Tooltip content={props => <CustomTooltip {...props} formatValue={formatValue} nameLabel="Year" metricLabel={metricLabel} sort="value-desc" selectedDataKeys={selectedDataKeys} columnKeySuffix={columnKeySuffix} columnLabels={columnLabels} tag={tag} />} />

            {stacked ? <>
                {[...series].map(item => {
            const isSelected = selectedDataKeys.includes(item.key);
            const hasSelection = selectedDataKeys.length > 0;
            const fillOpacity = hasSelection ? isSelected ? 0.8 : 0.3 : 0.6;
            const strokeOpacity = hasSelection ? isSelected ? 0.9 : 0.2 : 0.9;
            return <Area key={item.key} dataKey={item.key} stackId="stacked" name={item.name ?? item.key} fill={item.color} fillOpacity={fillOpacity} stroke={item.color} strokeWidth={2} strokeOpacity={strokeOpacity} isAnimationActive={isAnimationActive} animationEasing={ANIMATION_EASING} animationDuration={ANIMATION_DURATION} />;
          })}
              </> : <>
                {renderBands()}
                {renderAreaGradient()}
                {drawOrderedSeries.map(item => {
            const isSelected = selectedDataKeys.includes(item.key);
            const hasSelection = selectedDataKeys.length > 0;
            // de-emphasis is carried by strokeOpacity, so a series keeps its relative weight while dimmed
            const strokeWidth = hasSelection ? isSelected ? item.emphasisStrokeWidth ?? 4 : item.baseStrokeWidth ?? 1.5 : item.baseStrokeWidth ?? 3;
            const strokeOpacity = hasSelection ? isSelected ? 1 : 0.5 : 1;
            return <Line key={item.key} type="monotone" dataKey={item.key} stroke={item.color} strokeWidth={strokeWidth} strokeOpacity={strokeOpacity} strokeDasharray={item.alwaysSolid ? undefined : item.strokeDasharray ?? (!isSelected && hasSelection ? "6 3" : undefined)} dot={false} isAnimationActive={isAnimationActive} animationEasing={ANIMATION_EASING} animationDuration={ANIMATION_DURATION} name={item.name ?? item.key} />;
          })}
                {onSeriesClick && [...series].filter(item => !item.band).map(item => <Line key={`${item.key}-hit`} type="monotone" dataKey={item.key} stroke={item.color} strokeWidth={12} strokeOpacity={0} dot={false} activeDot={false} legendType="none" tooltipType="none" isAnimationActive={false} onClick={() => onSeriesClick(item.key)} style={{
            cursor: "pointer",
            pointerEvents: "stroke"
          }} name={item.name ?? item.key} />)}
              </>}

            {showReferenceLine && selectedYear !== undefined && <ReferenceLine x={selectedYear} stroke={TICK_COLOR} strokeWidth={1} strokeDasharray="2 3" label={<CustomReferenceLabel value={selectedYear} />} />}
          </ComposedChart>}
      </ResponsiveContainer>
    </div>;
};